import { type RedeemableItem } from '@koala/sdk';
import { useDispatch, useSelector } from 'react-redux';
import { OrderNowCTA } from '../orderNowCTA';
import LoyaltyAccessor from '@/components/account/loyaltyAccessor';
import { DonateToSocialCause } from '@/components/account/rewards/donate-to-social-cause/donate-to-social-cause';
import { ScanCode } from '@/components/account/rewards/scan-code';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import { Modal } from '@/components/uielements/modal';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { Render } from '@/components/uielements/render';
import {
  StyledBorderBottom,
  StyledButtonContainer,
  StyledCashButtonContainer,
  StyledReward,
  StyledRewardDescription,
  StyledRewardImage,
  StyledRewardsFlexContainer,
  StyledRewardsHeader,
  StyledRewardsHeaderContainer,
  StyledRewardTopContent,
} from '@/components/uielements/reward/styles';
import {
  LOYALTY_ACCESSOR_TYPES,
  LOYALTY_FEATURES,
  PROGRAM_TYPES,
  REWARD_TYPE,
} from '@/constants/loyalty';
import { useStrings } from '@/redux/cmsConfig';
import meActions from '@/redux/me/actions';
import { meSliceSelector } from '@/redux/me/reducer';
import { organizationSliceSelector } from '@/redux/organization/reducer';
import { getActiveRewardsAndOffersLength } from '@/utils/loyalty';
import { pluralizeString, safelyGetString } from '@/utils/stringHelpers';

export const MyRewards = () => {
  const { redeemables, loyaltyState } = useSelector(meSliceSelector);
  const strings = useStrings();
  const organization = useSelector(organizationSliceSelector);
  const dispatch = useDispatch();

  const currentPoints = loyaltyState?.points;

  // Only count rewards that user has enough points for
  const myActiveRedeemables = getActiveRewardsAndOffersLength(
    redeemables,
    [],
    /** @TODO ensure that `currentPoints` is defined. */
    // @ts-expect-error
    currentPoints,
  );

  // Punchh program types
  const programType = organization?.loyalty.program_type;

  // Only render if 'Redeemables' reward program
  if (programType !== PROGRAM_TYPES.POINTS_CONVERT_REDEEMABLES) {
    return null;
  }

  return (
    <>
      {/* Header */}
      {redeemables && (
        <StyledRewardsHeaderContainer>
          <StyledRewardsHeader>
            <StringAccessor accessor="rewards.my_rewards_header" html={true} />
            &nbsp;({myActiveRedeemables})
          </StyledRewardsHeader>
          <StringAccessor tag="p" accessor="rewards.my_rewards_subheader" html={true} />
          <StyledBorderBottom />
        </StyledRewardsHeaderContainer>
      )}

      {/* Rewards */}
      <StyledRewardsFlexContainer>
        {redeemables.map((reward: RedeemableItem) => {
          const rewardNotRedeemable =
            /** @TODO ensure that `points` is defined. */
            // @ts-expect-error
            reward.points_required > loyaltyState?.points;
          const pointsCounter = pluralizeString(
            safelyGetString(strings, 'rewards.rewards_counter'),
            reward.points_required > 1,
          );

          return (
            <StyledReward key={reward.id} $inactive={rewardNotRedeemable}>
              <StyledRewardTopContent>
                {reward.image?.url && (
                  <StyledRewardImage $inactive={rewardNotRedeemable}>
                    <img src={reward.image.url} alt={reward.label} />
                  </StyledRewardImage>
                )}
                <div>
                  <h3>{reward.label}</h3>
                  <h4>
                    {reward.points_required.toLocaleString()} {pointsCounter}
                  </h4>
                </div>
              </StyledRewardTopContent>
              <div>
                {reward.description &&
                  reward.description
                    .split('\n')
                    .map((line, i) => (
                      <StyledRewardDescription key={`line-${i}`}>
                        {line.trim()}
                      </StyledRewardDescription>
                    ))}
              </div>
              <StyledCashButtonContainer>
                <OrderNowCTA label="Rewards - Rewards" />

                {/* Only show 'Redeem in Store' CTA if supported */}
                <LoyaltyAccessor
                  checkType={LOYALTY_ACCESSOR_TYPES.FEATURE}
                  checkName={LOYALTY_FEATURES.CREATE_REDEMPTION_USING_REDEEMABLE}
                  component={
                    <Modal.Container
                      onCancel={() => dispatch(meActions.clearActiveRedemption())}
                      trigger={(open) => (
                        <StyledPrimaryButton
                          data-css-override="StyledPrimaryButton"
                          $buttonTheme="knockout"
                          aria-label={`Click to redeem reward ${reward.label} in store`}
                          onClick={open}
                        >
                          <StringAccessor accessor="rewards.redeem_in_store_button" />
                        </StyledPrimaryButton>
                      )}
                    >
                      {(toggle) => (
                        <ScanCode id={reward.id} type={REWARD_TYPE.REWARD} onError={toggle} />
                      )}
                    </Modal.Container>
                  }
                />
              </StyledCashButtonContainer>

              <Render condition={reward.supports_donation}>
                <StyledButtonContainer>
                  <DonateToSocialCause
                    donation={{
                      type: 'redeemable',
                      item: reward.id.toString(),
                    }}
                    rewardName={reward.label}
                  />
                </StyledButtonContainer>
              </Render>
            </StyledReward>
          );
        })}
      </StyledRewardsFlexContainer>
    </>
  );
};
